import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, CheckCircle2, AlertTriangle, XCircle, MinusCircle, Trash2, Plus, LogOut,
  Globe, HeartHandshake, Mail, CalendarClock, Sparkles, ArrowUpRight,
  RefreshCw, Send, Users, Link2, Share2, Lightbulb, Download, TrendingUp, Clock, Award, Copy,
  Radar, UserCog, LifeBuoy, DollarSign,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import UpgradeNudge from "../components/UpgradeNudge";
import UpgradeSuccess from "../components/UpgradeSuccess";
import { ChangePasswordModal } from "../components/ChangePasswordModal";
import NotificationPreferences from "../components/NotificationPreferences";
import MonitoringExclusions from "../components/MonitoringExclusions";
import AccountDetails from "../components/AccountDetails";
import OnboardingChecklist from "../components/OnboardingChecklist";
import { OneThingCard } from "../components/OneThingCard";
import { LoadingScreen } from "../components/LoadingScreen";
import { DashboardMobileNav } from "../components/DashboardMobileNav";
import CriticalMonitoringSection from "../components/CriticalMonitoringSection";
import ReportSummaryCard from "../components/ReportSummaryCard";
import RecommendationsSection from "../components/RecommendationsSection";
import ActionableCheckCard from "../components/ActionableCheckCard";
import ScanIdChip from "../components/ScanIdChip";
import ReportNavBar, { checkMatchesQuery } from "../components/ReportNavBar";
import { useScanLabels } from "../hooks/useScanLabels";

const ICONS = {
  website: Globe, donation: HeartHandshake, contact: Mail, ssl: ShieldCheck, domain: Globe,
  links: Link2, broken_links: Link2, social: Share2,
  security: ShieldCheck, donations: HeartHandshake, quality: Link2,
  governance: Users, seo: ArrowUpRight, accessibility: CheckCircle2,
};

const PLAN_LABEL = { essential: "Monthly Checkup", complete: "Protection" };
const planLabel = (p) => PLAN_LABEL[p] || (p ? p.charAt(0).toUpperCase() + p.slice(1) : "");

function StatusIcon({ status }) {
  if (status === "green") return <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" strokeWidth={2} />;
  if (status === "amber") return <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" strokeWidth={2} />;
  if (status === "red") return <XCircle className="w-4 h-4 text-red-500 shrink-0" strokeWidth={2} />;
  return <MinusCircle className="w-4 h-4 text-charcoal-300 shrink-0" strokeWidth={2} />;
}

const SCORE_STYLES = {
  green: "bg-emerald-50 border-emerald-100 text-emerald-600",
  amber: "bg-amber-50 border-amber-100 text-amber-600",
  red: "bg-red-50 border-red-100 text-red-600",
  info: "bg-charcoal-100 border-black/5 text-charcoal-400",
};

export default function Dashboard() {
  const { user, logout, refresh } = useAuth();
  // Iter-32 Phase 4: customer-facing terminology from the swappable
  // labels endpoint. Falls back to hardcoded defaults if the fetch
  // fails so the dashboard never breaks over a label-endpoint hiccup.
  const scanLabels = useScanLabels();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [org, setOrg] = useState(null);
  // Snapshot of the last SERVER-KNOWN website URL. Compared against the freshly-saved
  // value to detect an actual change (the org state is a controlled-input draft and
  // changes on every keystroke, so comparing it directly would always miss).
  const savedWebsiteRef = useRef(null);
  // Session-scoped guard so the "Turn on Protection" post-report prompt fires at most once
  // per browser session (not every time the Checkup user runs their manual scan).
  const postReportPromptShownRef = useRef(false);
  const [reminders, setReminders] = useState([]);
  const [report, setReport] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [savingOrg, setSavingOrg] = useState(false);
  const [newRem, setNewRem] = useState({ title: "", date: "" });
  const [running, setRunning] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [team, setTeam] = useState({ recipients: [], is_complete: false });
  const [newMember, setNewMember] = useState("");
  const [history, setHistory] = useState(null);
  const [verification, setVerification] = useState(null);
  const [scanUsage, setScanUsage] = useState(null);
  const [upgradeNudge, setUpgradeNudge] = useState(false);
  const [upgradeNudgeReason, setUpgradeNudgeReason] = useState("proactive");
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(null);
  const [milestonesUnseen, setMilestonesUnseen] = useState(0);
  // Which check-item lists the user has clicked "+N more" on. Keyed by
  // check `key` so the state survives re-renders and doesn't tie the
  // expanded/collapsed decision to array index.
  const [expandedItems, setExpandedItems] = useState(() => new Set());
  // Iter-38 Stage 3: report search query. Empty by default; when set, the
  // category loop below filters checks via `checkMatchesQuery`. This is a
  // convenience feature — not the primary navigation model.
  const [reportSearch, setReportSearch] = useState("");
  const orgProfileRef = useRef(null);

  const scrollToOrgProfile = useCallback(() => {
    if (orgProfileRef.current) {
      orgProfileRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      // Focus the website field for immediate keyboard entry.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="org-website"]');
        if (el) el.focus();
      }, 450);
    }
  }, []);

  const openUpgradeModal = useCallback((reason = "proactive") => {
    setUpgradeNudgeReason(reason);
    setUpgradeNudge(true);
  }, []);

  const onUpgradeComplete = useCallback(async () => {
    // Called by UpgradeNudge when the backend swaps the plan directly. Close the
    // nudge, open the celebratory success modal, and refresh the plan/team data
    // in the background so the dashboard reflects Complete instantly.
    setUpgradeNudge(false);
    setUpgradeSuccess(true);
    try {
      await refresh();
      const [s, t] = await Promise.all([api.get("/subscription"), api.get("/team")]);
      setSubscription(s.data);
      setTeam(t.data);
    } catch { /* non-fatal */ }
  }, [refresh]);

  const load = useCallback(async () => {
    // Fetch EVERYTHING the dashboard needs for its first paint in one shot.
    // Historically we fetched `/health-report` in a separate step after
    // dropping the loading screen — which briefly rendered the dashboard
    // with `report === null` and caused a placeholder flash on hard refresh.
    // Bundling into one Promise.all extends the loading screen by ~200ms
    // (usually) but eliminates the flash. `/health-report` is fast unless
    // it's running a live checkup — the endpoint short-circuits to a
    // cached read for anything <5min old.
    try {
      const [o, r, s, t, h] = await Promise.all([
        api.get("/organization"), api.get("/reminders"),
        api.get("/subscription"), api.get("/team"),
        api.get("/health-report").catch(() => ({ data: null })),
      ]);
      setOrg(o.data || {});
      savedWebsiteRef.current = (o.data && o.data.website) || "";
      setReminders(r.data);
      setSubscription(s.data);
      setTeam(t.data);
      if (h.data) setReport(h.data);
    } finally {
      // Never leave the loading overlay stuck — even if the primary APIs fail
      // (offline, auth expired, 500), the underlying UI will render its own
      // error / empty states from that point on.
      setInitialLoading(false);
    }
    try {
      const [hist, ver, usage] = await Promise.all([
        api.get("/reports/history"),
        api.get("/verification"),
        api.get("/scan-usage"),
      ]);
      setHistory(hist.data);
      setVerification(ver.data);
      setScanUsage(usage.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load unseen-milestones count for the header nav badge. Independent of the
  // heavier `load()` pipeline so the badge appears the moment the dashboard opens.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get("/milestones").then(({ data }) => {
      if (!cancelled) setMilestonesUnseen(data?.unseen || 0);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  // Proactive upgrade nudge: for Essential subscribers, open the modal once per browser
  // session, 60 seconds after they land on the dashboard. Cleared on logout so the next
  // fresh sign-in restarts the timer. Skipped entirely for Complete customers.
  useEffect(() => {
    if (!user || user.plan !== "essential") return undefined;
    const sessionKey = `mw:proactive-nudge-shown:${user.id || user.email || "anon"}`;
    if (sessionStorage.getItem(sessionKey) === "1") return undefined;
    const timer = setTimeout(() => {
      openUpgradeModal("proactive");
      sessionStorage.setItem(sessionKey, "1");
    }, 60000);
    return () => clearTimeout(timer);
  }, [user, openUpgradeModal]);

  // Poll while a background whole-site scan is running (Complete plan)
  useEffect(() => {
    if (!report?.scanning) return undefined;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get("/health-report");
        if (!data.scanning) {
          setReport(data);
          api.get("/reports/history").then((h) => setHistory(h.data)).catch(() => {});
          api.get("/verification").then((v) => setVerification(v.data)).catch(() => {});
          // Refresh the manual-scan counter from the DB (source of truth) — the
          // pre-decrement in /run-checkup was optimistic; this reconciles.
          api.get("/scan-usage").then((u) => setScanUsage(u.data)).catch(() => {});
          toast.success("Whole-site scan complete — your Mission Watch Score is updated");
        }
      } catch { /* ignore */ }
    }, 8000);
    return () => clearInterval(iv);
  }, [report?.scanning]);

  // Stripe return polling
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (data.payment_status === "paid" || data.payment_status === "no_payment_required" || data.status === "complete") {
          toast.success("Your free trial has started — welcome to Mission Watch!");
          await refresh();
          setParams({}, { replace: true });
          return;
        }
        if (data.status === "expired" || attempts >= 5) {
          toast.error("Payment not completed.");
          setParams({}, { replace: true });
          return;
        }
      } catch { /* ignore */ }
      attempts += 1;
      setTimeout(poll, 2000);
    };
    toast.loading("Confirming payment…", { id: "pay" });
    poll().finally(() => toast.dismiss("pay"));
    // Intentional: run once on mount when the Stripe redirect lands. Adding
    // params/refresh/setParams to deps would re-trigger the poll on every
    // URL change and toast update, defeating the "once per return" contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveOrg = async (e) => {
    e.preventDefault();
    setSavingOrg(true);
    const prevWebsite = savedWebsiteRef.current || "";
    try {
      const { data } = await api.put("/organization", {
        organization_name: org.organization_name || "",
        website: org.website || "", donation_url: org.donation_url || "",
        contact_form_url: org.contact_form_url || "",
        social_links: org.social_links || {},
        custom_links: (org.custom_links || []).filter((l) => (l.url || "").trim()),
      });
      setOrg(data);
      // Confirm coverage whenever the URL actually changed from what the server had.
      const websiteChanged = data.website && data.website !== prevWebsite;
      savedWebsiteRef.current = data.website || "";
      if (websiteChanged) {
        toast.success(`Coverage confirmed for ${data.website} — we'll scan every donation form we find plus up to 150 other pages.`, { duration: 8000 });
      } else {
        toast.success("Organization saved");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSavingOrg(false); }
  };

  const addReminder = async (e) => {
    e.preventDefault();
    if (!newRem.title || !newRem.date) return;
    try {
      const { data } = await api.post("/reminders", newRem);
      setReminders([...reminders, data].sort((a, b) => a.date.localeCompare(b.date)));
      setNewRem({ title: "", date: "" });
      toast.success("Reminder added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const delReminder = async (id) => {
    await api.delete(`/reminders/${id}`);
    setReminders(reminders.filter((r) => r.id !== id));
  };

  const runCheckup = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/run-checkup");
      if (typeof data.manual_remaining === "number") {
        setScanUsage((s) => ({
          ...(s || {}),
          used: data.manual_limit - data.manual_remaining,
          limit: data.manual_limit,
          remaining: data.manual_remaining,
        }));
      }
      if (data.scanning) {
        toast.info("Whole-site scan started — this can take a few minutes. You can keep using the dashboard.");
        setReport((r) => ({ ...(r || {}), scanning: true, has_run: data.has_run ?? r?.has_run }));
        return;
      }
      setReport(data);
      api.get("/reports/history").then((h) => setHistory(h.data)).catch(() => {});
      api.get("/verification").then((v) => setVerification(v.data)).catch(() => {});
      api.get("/scan-usage").then((u) => setScanUsage(u.data)).catch(() => {});
      if (data.new_issues > 0) {
        const alerted = data.alerts_sent > 0 ? " — alert emailed" : "";
        toast.warning(`Score updated: ${data.new_issues} new issue${data.new_issues === 1 ? "" : "s"} found${alerted}`);
      } else {
        toast.success("Mission Watch Score updated — everything looks healthy");
      }
      // After the first report is generated, prompt Checkup users to turn on Protection.
      // Once per report (guarded by an in-memory ref so it doesn't re-fire on every scan).
      if (user?.plan === "essential" && !postReportPromptShownRef.current) {
        postReportPromptShownRef.current = true;
        setTimeout(() => openUpgradeModal("post-report"), 1800);
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429 && detail && typeof detail === "object" && detail.message) {
        toast.error(detail.message, { duration: 8000 });
        setScanUsage((s) => ({ ...(s || {}), used: detail.used, limit: detail.limit,
                              remaining: 0, reset_at: detail.reset_at }));
        // Essential users at cap → offer the natural upsell to Complete
        if (user?.plan === "essential") {
          openUpgradeModal("cap");
        }
      } else {
        toast.error(formatApiError(detail));
      }
    } finally { setRunning(false); }
  };

  const downloadPdf = () => {
    window.print();
  };

  const emailReport = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/report/send");
      if (data.ok === false) {
        toast.error(data.error || "We couldn't deliver the email. Please try again.", { duration: 10000 });
      } else if (data.failed && data.failed.length) {
        toast.warning(`Report sent to ${data.sent}. Couldn't deliver to: ${data.failed.join(", ")}`);
      } else {
        toast.success(`Report emailed to ${data.sent} recipient${data.sent === 1 ? "" : "s"}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail), { duration: 10000 });
    } finally { setEmailing(false); }
  };

  const manageBilling = () => {
    // Route to the branded /billing page. From there the customer can swap plans,
    // cancel with a save-the-sale flow, or jump to Stripe for payment-method edits.
    navigate("/billing");
  };

  const addTeam = async (e) => {
    e.preventDefault();
    if (!newMember) return;
    try {
      const { data } = await api.post("/team", { email: newMember });
      setTeam({ ...team, recipients: data.recipients, members: data.members });
      setNewMember("");
      if (data.invited) toast.success(`Invite sent to ${newMember}`);
      else toast.warning(`Added ${newMember} — invite email couldn't be delivered. You can resend below.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeTeam = async (email) => {
    const { data } = await api.delete(`/team/${encodeURIComponent(email)}`);
    setTeam({ ...team, recipients: data.recipients, members: data.members });
  };

  const resendInvite = async (email) => {
    setResendingInvite(email);
    try {
      const { data } = await api.post(`/team/${encodeURIComponent(email)}/resend`);
      setTeam((t) => ({ ...t, members: data.members }));
      if (data.ok) toast.success(`Invite resent to ${email}`);
      else toast.warning(`Couldn't deliver — check the address and try again.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setResendingInvite(null); }
  };

  const setField = (k) => (e) => setOrg({ ...org, [k]: e.target.value });
  const setSocial = (k) => (e) => setOrg({ ...org, social_links: { ...(org.social_links || {}), [k]: e.target.value } });
  const addCustomLink = () => {
    const links = org.custom_links || [];
    if (links.length >= 5) { toast.error("You can add up to 5 extra links."); return; }
    setOrg({ ...org, custom_links: [...links, { label: "", url: "" }] });
  };
  const setCustomLink = (i, k) => (e) => {
    const links = [...(org.custom_links || [])];
    links[i] = { ...links[i], [k]: e.target.value };
    setOrg({ ...org, custom_links: links });
  };
  const removeCustomLink = (i) => {
    const links = [...(org.custom_links || [])];
    links.splice(i, 1);
    setOrg({ ...org, custom_links: links });
  };

  const active = user?.subscription_status === "active";
  const isTrial = subscription?.is_trialing;
  const trialDaysLeft = subscription?.trial_end
    ? Math.max(0, Math.ceil((subscription.trial_end * 1000 - Date.now()) / 86400000))
    : null;
  const billingDate = subscription?.trial_end
    ? new Date(subscription.trial_end * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  // BULLETPROOF FIRST PAINT: while the primary APIs are in flight we render
  // ONLY the LoadingScreen — the placeholder dashboard never mounts. This
  // eliminates the "0 of 4 done" / "Your Organization" flash on hard refresh.
  if (initialLoading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-charcoal-50">
      {/* top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/5">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 rounded-lg bg-charcoal-900 grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-semibold tracking-tight text-charcoal-900 text-[15px]">Mission Watch</span>
          </Link>

          {/* Mobile hamburger — opens the DashboardMobileNav sheet.
              Hidden on ≥ sm where the inline nav below takes over. */}
          <DashboardMobileNav
            user={user}
            milestonesUnseen={milestonesUnseen}
            onOpenAccount={() => setShowChangePassword(true)}
            onSignOut={logout}
          />

          {/* Desktop nav — hidden on < sm so the mobile hamburger doesn't collide. */}
          <div className="hidden sm:flex items-center gap-3">
            <span className="hidden md:inline text-sm text-charcoal-500">{user?.email}</span>
            <Link
              to="/milestones"
              className="relative inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-charcoal-900 px-3 py-2 rounded-lg hover:bg-charcoal-100 transition-colors"
              data-testid="milestones-nav-link"
              title="Your Mission Watch milestones"
            >
              <Award className="w-4 h-4" /> Milestones
              {milestonesUnseen > 0 && (
                <span
                  className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold leading-none tabular-nums"
                  data-testid="milestones-unseen-badge"
                >
                  {milestonesUnseen > 9 ? "9+" : milestonesUnseen}
                </span>
              )}
            </Link>
            <a
              href="mailto:admin@missionwatch.app?subject=Mission%20Watch%20Support"
              className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-charcoal-900 px-3 py-2 rounded-lg hover:bg-charcoal-100 transition-colors"
              data-testid="support-link"
              title="Email support at admin@missionwatch.app"
            >
              <LifeBuoy className="w-4 h-4" /> Support
            </a>
            {user?.is_owner && (
              <Link to="/admin/cost"
                className="inline-flex items-center gap-1.5 text-sm text-brand hover:underline px-3 py-2 rounded-lg hover:bg-charcoal-100 transition-colors"
                data-testid="owner-cost-link"
                title="Cost dashboard">
                <DollarSign className="w-4 h-4" /> Cost dashboard
              </Link>
            )}
            <button
              onClick={() => setShowChangePassword(true)}
              className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-charcoal-900 px-3 py-2 rounded-lg hover:bg-charcoal-100 transition-colors"
              data-testid="account-settings-btn"
              title="Change your password"
            >
              <UserCog className="w-4 h-4" /> Account
            </button>
            <button onClick={() => { logout(); navigate("/"); }}
              className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-charcoal-900 px-3 py-2 rounded-lg hover:bg-charcoal-100 transition-colors"
              data-testid="logout-btn"
              title="Sign out">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="dashboard">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">Dashboard</p>
            <h1 className="text-3xl font-semibold tracking-tight text-charcoal-900 mt-1">
              {org?.organization_name || "Your Organization"}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <div className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border text-sm font-medium ${active ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-amber-50 border-amber-100 text-amber-700"}`} data-testid="plan-badge">
              <span className={`w-2 h-2 rounded-full ${active ? "bg-emerald-500 dot-pulse" : "bg-amber-500"}`} />
              {active
                ? (isTrial
                    ? `${planLabel(user?.plan)} trial · ${trialDaysLeft} days left`
                    : `${planLabel(user?.plan)} plan active`)
                : (subscription?.status === "canceled" ? "Plan canceled" : "No active plan")}
            </div>
            {active && user?.plan === "essential" && (
              <button
                onClick={() => openUpgradeModal("header")}
                className="inline-flex items-center gap-1.5 text-sm px-3.5 py-2 rounded-full bg-brand text-white font-medium hover:bg-brand-hover transition-colors shadow-soft"
                data-testid="header-upgrade-btn"
              >
                Turn on Protection <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            )}
            {active && (
              <>
                {user?.stripe_customer_id && (
                  <button onClick={manageBilling}
                    className="text-sm px-3 py-2 rounded-full border border-black/10 text-charcoal-600 hover:text-charcoal-900 hover:border-black/20 transition-colors"
                    data-testid="manage-billing-btn">
                    Manage billing
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Failed payment banner — appears when Stripe reports past_due / unpaid / incomplete.
             Nothing pushes as hard as a broken donation page, so we keep the tone
             calm-but-urgent and route the CTA straight to the Stripe billing portal. */}
        {["past_due", "unpaid", "incomplete"].includes(subscription?.status) && (
          <div className="mb-6 rounded-2xl bg-red-50 border border-red-200 p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4" data-testid="failed-payment-banner">
            <div className="flex items-start gap-3 max-w-xl">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-red-800">Your last payment didn't go through.</p>
                <p className="text-sm text-red-700/90 mt-1 leading-relaxed">
                  Your Mission Watch monitoring stays on for a short grace period. Update your card now to avoid any interruption in donation-link checks.
                </p>
              </div>
            </div>
            <button
              onClick={manageBilling}
              className="px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors shadow-soft"
              data-testid="fix-payment-btn"
            >
              Update payment method
            </button>
          </div>
        )}

        {/* Milestone celebration banner — appears whenever the milestone framework
             fires a new event (Complete+ growth, 1-year anniversary, perfect score,
             6-months no-critical, etc.). Positive, celebratory tone by design. */}
        {(subscription?.latest_milestone || subscription?.complete_plus_exceeded) && (
          <div className="mb-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100/70 border border-emerald-200 p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4" data-testid="milestone-banner">
            <div className="flex items-start gap-3 max-w-2xl">
              <Sparkles className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-emerald-700 mb-1">
                  Mission Watch Milestone
                </p>
                <p className="font-medium text-emerald-900">
                  {subscription?.latest_milestone?.headline
                    || "Congratulations — your organization has reached a new milestone."}
                </p>
                <p className="text-sm text-emerald-800/90 mt-1 leading-relaxed">
                  {subscription?.latest_milestone?.subline
                    || `We've detected approximately ${subscription?.complete_plus_pages || "150+"} pages across your website. Your donation pages continue receiving priority monitoring every hour — and when you're ready, Complete+ was designed for organizations that have grown to this stage.`}
                </p>
              </div>
            </div>
            {subscription?.complete_plus_exceeded && (
              <a
                href="/#contact"
                className="px-5 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium transition-colors shadow-soft whitespace-nowrap"
                data-testid="upgrade-complete-plus-btn"
              >
                Learn about Complete+
              </a>
            )}
          </div>
        )}

        {/* Onboarding checklist — appears for new signups until all 4 steps done */}
        <OnboardingChecklist
          user={user}
          subscription={subscription}
          org={org}
          report={report}
          onStartTrial={() => navigate("/get-started")}
          onFocusWebsite={scrollToOrgProfile}
          onRunCheckup={runCheckup}
        />

        {/* trial start banner */}
        {!active && (
          <div className="mb-8 rounded-2xl bg-charcoal-900 text-white p-6 sm:p-7 flex flex-wrap items-center justify-between gap-4" data-testid="upgrade-banner">
            <div className="flex items-start gap-3 max-w-xl">
              <Sparkles className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Start protecting your organization today</p>
                <p className="text-sm text-white/60 mt-1">Add a card to unlock unlimited Mission Watch Scores, instant alerts, and monthly reports. 7-day free trial — cancel anytime before your first billing date.</p>
              </div>
            </div>
            <button onClick={() => navigate("/get-started")} className="px-5 py-2.5 rounded-xl bg-brand hover:bg-brand-hover text-sm font-medium transition-colors" data-testid="start-trial-btn">
              Start Free Trial
            </button>
          </div>
        )}

        {/* trial status note — hidden when the milestone banner is already
             on screen. Both are welcome/reassurance signals; showing them
             stacked was competing for attention on first load. Trial info
             is still shown in the top-right plan badge either way. */}
        {active && isTrial && billingDate
            && !(subscription?.latest_milestone || subscription?.complete_plus_exceeded)
            && (
          <div className="mb-8 rounded-xl bg-emerald-50 border border-emerald-100 px-5 py-3.5 flex items-center gap-2.5" data-testid="trial-note">
            <Sparkles className="w-4 h-4 text-emerald-600 shrink-0" />
            <p className="text-sm text-emerald-800">
              You're on a free trial — <span className="font-medium">{trialDaysLeft} days left</span>. Your first payment is on {billingDate}. Cancel anytime before then and you won't be charged.
            </p>
          </div>
        )}

        {/* Persistent Protection-plan upsell — always visible on the dashboard for Checkup
             users so the upgrade is one click away without waiting for a nudge to appear. */}
        {active && user?.plan === "essential" && (
          <div className="mb-8 rounded-2xl bg-gradient-to-br from-charcoal-900 to-charcoal-800 text-white p-6 sm:p-7 relative overflow-hidden" data-testid="complete-upsell-card">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand/20 blur-3xl pointer-events-none" />
            <div className="relative flex flex-wrap items-center justify-between gap-5">
              <div className="max-w-xl">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/25 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 dot-pulse" />
                  <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">You're on Monthly Checkup</p>
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold tracking-tight leading-tight">
                  Want us to <span className="text-emerald-400">keep watching while you sleep?</span>
                </h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">
                  Protection checks your website, donation pages, and contact forms every hour
                  and alerts you the moment something breaks — even on weekends and holidays.
                </p>
              </div>
              <div className="flex flex-col items-start sm:items-end gap-2">
                <p className="text-[11px] font-mono text-white/50">$20 more per month</p>
                <button
                  onClick={() => openUpgradeModal("dashboard-card")}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors shadow-soft"
                  data-testid="dashboard-upsell-btn"
                >
                  Turn On Hourly Protection <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {report?.scanning && (
          <div className="mb-6 rounded-2xl bg-brand/5 border border-brand/20 px-5 py-4 flex items-center gap-3" data-testid="scan-progress-banner">
            <RefreshCw className="w-4 h-4 text-brand animate-spin shrink-0" />
            <div>
              <p className="text-sm font-medium text-charcoal-800">Full-site inspection in progress…</p>
              <p className="text-xs text-charcoal-500 mt-0.5">Mission Watch is inspecting your full site (up to 150 pages). Your Mission Watch Score updates automatically when the inspection finishes — this can take a few minutes.</p>
            </div>
          </div>
        )}

        {report?.has_run && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-strip">
            <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-5">
              <div className="flex items-center gap-2 text-charcoal-400"><Award className="w-4 h-4" /><span className="text-[11px] font-mono uppercase tracking-[0.14em]">Score</span></div>
              <p className="mt-2 text-3xl font-semibold tracking-tighter text-charcoal-900">{report.overall_score}<span className="text-base text-charcoal-400 font-normal">/100</span></p>
            </div>
            <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-5">
              <div className="flex items-center gap-2 text-charcoal-400"><TrendingUp className="w-4 h-4" /><span className="text-[11px] font-mono uppercase tracking-[0.14em]">Website health</span></div>
              <p className="mt-2 text-3xl font-semibold tracking-tighter text-charcoal-900" data-testid="stats-website-health">
                {report.checks_passing ?? "—"}
                <span className="text-base text-charcoal-400 font-normal"> of {report.checks_total ?? "—"} healthy</span>
              </p>
            </div>
            <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-5">
              <div className="flex items-center gap-2 text-charcoal-400"><Clock className="w-4 h-4" /><span className="text-[11px] font-mono uppercase tracking-[0.14em]">Last checked</span></div>
              <p className="mt-2 text-sm font-medium text-charcoal-800" data-testid="dashboard-last-scan-at">{report.generated_at ? new Date(report.generated_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}</p>
              {active && (
                <p className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-emerald-700" data-testid="dashboard-actively-monitoring">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dot-pulse" />
                  Actively monitoring
                </p>
              )}
              {history?.next_scan && active && (
                <p className="mt-1 text-[11px] font-mono text-charcoal-400">Next: {new Date(history.next_scan).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
              )}
            </div>
            <div className="rounded-2xl bg-white border border-black/5 shadow-soft p-5" data-testid="health-report-card-strip">
              <div className="flex items-center gap-2 text-charcoal-400"><TrendingUp className="w-4 h-4" /><span className="text-[11px] font-mono uppercase tracking-[0.14em]">{scanLabels.tiers("weekly")}</span></div>
              {history?.history?.length >= 2 ? (
                <div className="mt-3 flex items-end gap-1.5" data-testid="trend-dots">
                  {[...history.history].reverse().slice(-8).map((h, i) => (
                    <div key={i} title={`${h.period}: ${h.score}`}
                      className={`flex-1 rounded-sm ${h.status === "green" ? "bg-emerald-400" : h.status === "amber" ? "bg-amber-400" : h.status === "red" ? "bg-red-400" : "bg-charcoal-200"}`}
                      style={{ height: `${Math.max(8, (h.score || 0) * 0.36)}px` }} />
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-sm text-charcoal-400">Not enough history yet — your Health Report will show trend once we have more data.</p>
              )}
              {(history?.history?.length || 0) >= 1 && (
                <Link
                  to="/history"
                  className="mt-3 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-brand hover:underline"
                  data-testid="view-history-link"
                >
                  View all reports <ArrowUpRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>
        )}

        <div className="mb-8"><OneThingCard /></div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* health report */}
          <section id="health-report" className="lg:col-span-2 min-w-0 rounded-2xl bg-white border border-black/5 shadow-soft p-5 sm:p-6" data-testid="health-report-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-charcoal-400">
                  {report?.has_run ? "Latest Mission Watch Score" : "Mission Watch Score"}{report?.period ? ` · ${report.period}` : ""}
                </p>
                <h2 className="text-lg font-medium text-charcoal-900 mt-0.5">Your Mission Watch Score</h2>
                {report?.has_run && report?.scan_id && (
                  <div className="mt-1" data-testid="customer-scan-id">
                    <ScanIdChip scanId={report.scan_id} showCopy={false} testid="customer-scan-id-chip" />
                  </div>
                )}
              </div>
              {/* Actions row — wraps on narrow viewports so the "Run Mission Watch
                  Score" button never gets pushed off-screen. */}
              <div className="flex flex-wrap items-center gap-2">
                {report?.has_run && (
                  <>
                    <button onClick={emailReport} disabled={emailing}
                      className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-black/10 text-charcoal-700 hover:bg-charcoal-50 transition-colors disabled:opacity-60"
                      data-testid="email-report-btn">
                      <Send className="w-3.5 h-3.5" /> {emailing ? "Sending…" : "Email"}
                    </button>
                    <button onClick={downloadPdf}
                      className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-black/10 text-charcoal-700 hover:bg-charcoal-50 transition-colors no-print"
                      data-testid="download-pdf-btn">
                      <Download className="w-3.5 h-3.5" /> PDF
                    </button>
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${SCORE_STYLES[report?.overall_status] || SCORE_STYLES.info}`} data-testid="health-score">
                      <span className="text-2xl font-semibold text-charcoal-900 tracking-tighter">{report?.overall_score}</span>
                      <span className="text-xs font-mono">/100</span>
                    </div>
                  </>
                )}
                <button onClick={runCheckup} disabled={running || report?.scanning || scanUsage?.remaining === 0}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-charcoal-900 text-white hover:bg-charcoal-800 transition-colors disabled:opacity-60"
                  data-testid="run-checkup-btn"
                  title={scanUsage?.remaining === 0 ? "Manual refresh limit reached this month" : undefined}>
                  <RefreshCw className={`w-3.5 h-3.5 ${running || report?.scanning ? "animate-spin" : ""}`} />
                  {/* Shorter label on very small viewports so this button
                      doesn't overflow the card on mobile. */}
                  <span className="sm:hidden">
                    {report?.scanning ? "Inspecting…" : running ? "Checking…" : "Refresh"}
                  </span>
                  <span className="hidden sm:inline">
                    {report?.scanning ? "Inspecting…" : running ? "Checking…" : "Run Mission Watch Score"}
                  </span>
                </button>
              </div>
            </div>
            {active && scanUsage && scanUsage.limit > 0 && (
              <div className="mb-4 -mt-1 flex flex-wrap items-center gap-2 text-[11px] font-mono text-charcoal-400" data-testid="scan-usage-strip">
                <span className={scanUsage.remaining === 0 ? "text-red-500 font-medium" : ""}>
                  {scanUsage.remaining} of {scanUsage.limit} manual scans left this month
                </span>
                {scanUsage.remaining === 0 && scanUsage.reset_at && (
                  <span className="text-charcoal-500">
                    · resets {new Date(scanUsage.reset_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </span>
                )}
                {scanUsage.remaining > 0 && (
                  <span className="text-charcoal-300">· automatic sweeps keep running regardless</span>
                )}
                {scanUsage.remaining === 0 && user?.plan === "essential" && (
                  <button onClick={() => openUpgradeModal("cap")}
                    className="ml-auto inline-flex items-center gap-1 text-brand hover:underline font-medium normal-case"
                    data-testid="scan-usage-upgrade-cta">
                    Turn on hourly Protection <ArrowUpRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}

            {report === null ? (
              <div className="py-12 text-center text-sm text-charcoal-400 font-mono" data-testid="report-loading">Loading report…</div>
            ) : report?.has_run ? (
              <>
                <ReportSummaryCard report={report} />
                <RecommendationsSection report={report} />
                <ReportNavBar
                  categories={report?.categories || []}
                  query={reportSearch}
                  onQueryChange={setReportSearch}
                />
                <div className="space-y-5" data-testid="checks-by-category">
                  {/* Iter-32 Phase 4: split the report into two sections.
                      The top block is the every-5-min classified critical
                      sweep (host_unreachable + critical:<hash> checks) —
                      shown with the 5-state visualization + developer-
                      details expander. The block below is the existing
                      per-category daily audit (unchanged rendering).
                      Customer-facing tier names come from useScanLabels()
                      so the terminology pass edits one file. */}
                  <CriticalMonitoringSection
                    checks={report.checks}
                    fastSweepAt={report.fast_sweep_at}
                  />
                  {report.checks?.some(
                    (c) => c && !(c.key === "host_unreachable" || (typeof c.key === "string" && c.key.startsWith("critical:")))
                  ) && (
                    <div className="flex items-center gap-2 mb-1" data-testid="daily-audit-header">
                      <span className="w-2 h-2 rounded-full bg-charcoal-400" />
                      <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-charcoal-500">
                        {scanLabels.tiers("daily")}
                      </p>
                    </div>
                  )}
                  {reportSearch && (() => {
                    const _nc = (report.checks || []).filter(
                      (c) => c && !(c.key === "host_unreachable" || (typeof c.key === "string" && c.key.startsWith("critical:")))
                    );
                    const anyMatch = _nc.some((c) => checkMatchesQuery(c, reportSearch));
                    if (anyMatch) return null;
                    return (
                      <div
                        className="rounded-xl bg-charcoal-50/70 border border-dashed border-black/10 py-6 px-4 text-center"
                        data-testid="report-search-no-results"
                      >
                        <p className="text-sm text-charcoal-700">
                          No matches for &ldquo;{reportSearch}&rdquo; in this report.
                        </p>
                        <button
                          type="button"
                          onClick={() => setReportSearch("")}
                          className="mt-1 text-[12px] text-brand hover:underline"
                          data-testid="report-search-no-results-clear"
                        >
                          Clear search
                        </button>
                      </div>
                    );
                  })()}
                  {(report?.categories?.length ? report.categories : [{ key: null, name: "Checks", status: report?.overall_status }]).map((cat) => {
                    // Exclude critical-tier checks from the category loop —
                    // they render above in `CriticalMonitoringSection`.
                    const _nonCritical = (report.checks || []).filter(
                      (c) => c && !(c.key === "host_unreachable" || (typeof c.key === "string" && c.key.startsWith("critical:")))
                    );
                    const catChecksRaw = cat.key
                      ? _nonCritical.filter((c) => c.category === cat.key)
                      : _nonCritical;
                    // Iter-38 Stage 3: apply the search filter. When the query
                    // matches nothing in a category we hide the whole section
                    // (rather than showing an empty header) so the reader's
                    // scan pattern stays clean.
                    const catChecks = reportSearch
                      ? catChecksRaw.filter((c) => checkMatchesQuery(c, reportSearch))
                      : catChecksRaw;
                    if (!catChecks.length) return null;
                    const anchorKey = cat.key || cat.name;
                    return (
                      <div key={cat.key || "all"} id={`cat-${anchorKey}`} data-testid={`category-${anchorKey}`} className="scroll-mt-24">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${cat.status === "green" ? "bg-emerald-500" : cat.status === "amber" ? "bg-amber-500" : cat.status === "red" ? "bg-red-500" : "bg-charcoal-300"}`} />
                            <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-charcoal-500">{cat.name}</p>
                          </div>
                          {cat.total != null && (
                            <span className="text-[11px] font-mono text-charcoal-400">{cat.passing}/{cat.total} passing</span>
                          )}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-2">
                          {catChecks.map((c) => {
                            const Icon = ICONS[c.key] || ICONS[c.category] || CheckCircle2;
                            return (
                              <ActionableCheckCard
                                key={c.key}
                                check={c}
                                Icon={Icon}
                                user={user}
                                anchorId={`issue-${c.key}`}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-12 px-6 rounded-xl bg-charcoal-50/70 border border-dashed border-black/10" data-testid="report-empty-state">
                <Globe className="w-8 h-8 text-charcoal-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-charcoal-600 font-medium">
                  Your report is being prepared
                </p>
                <p className="text-sm text-charcoal-400 mt-1 max-w-md mx-auto leading-relaxed">
                  {org?.website
                    ? "We're running your first website check. Your Health Report will be ready shortly — we'll email you the moment it's done."
                    : "Add your website below to get started. Mission Watch will begin caring for your critical pages within minutes."}
                </p>
              </div>
            )}
          </section>

          {/* reminders */}
          <section className="rounded-2xl bg-white border border-black/5 shadow-soft p-6" data-testid="reminders-card">
            <div className="flex items-center gap-2 mb-4">
              <CalendarClock className="w-4 h-4 text-brand" />
              <h2 className="text-lg font-medium text-charcoal-900">Reminders</h2>
            </div>
            <form onSubmit={addReminder} className="space-y-2.5 mb-4">
              <input value={newRem.title} onChange={(e) => setNewRem({ ...newRem, title: e.target.value })}
                placeholder="e.g. Insurance renewal"
                className="w-full h-10 px-3 rounded-lg border border-black/10 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                data-testid="reminder-title" />
              <input type="date" value={newRem.date} onChange={(e) => setNewRem({ ...newRem, date: e.target.value })}
                className="w-full h-10 px-3 rounded-lg border border-black/10 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                data-testid="reminder-date" />
              <button type="submit" className="w-full h-10 rounded-lg bg-charcoal-900 text-white text-sm font-medium hover:bg-charcoal-800 transition-colors inline-flex items-center justify-center gap-1.5" data-testid="add-reminder-btn">
                <Plus className="w-4 h-4" /> Add reminder
              </button>
            </form>
            <div className="space-y-2">
              {reminders.length === 0 && <p className="text-sm text-charcoal-400">No reminders yet.</p>}
              {reminders.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-charcoal-50/70 border border-black/5" data-testid="reminder-item">
                  <div>
                    <p className="text-sm text-charcoal-800">{r.title}</p>
                    <p className="text-[11px] font-mono text-charcoal-400">{r.date}</p>
                  </div>
                  <button onClick={() => delReminder(r.id)} className="text-charcoal-300 hover:text-red-500 transition-colors" data-testid="delete-reminder-btn">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* org profile */}
        <section ref={orgProfileRef} className="mt-6 rounded-2xl bg-white border border-black/5 shadow-soft p-6" data-testid="org-profile-card">
          <h2 className="text-lg font-medium text-charcoal-900 mb-1">Organization profile</h2>
          <p className="text-sm text-charcoal-500 mb-5">The digital assets we monitor each month.</p>

          {/* Coverage confirmation — always visible so users trust the product is doing what
              they signed up for. Renders once a website URL is entered. */}
          {org?.website && (
            <div className="mb-6 rounded-xl bg-charcoal-900 text-white p-5 relative overflow-hidden" data-testid="coverage-card">
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
              <div className="relative flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/25 grid place-items-center shrink-0">
                  <Radar className="w-4 h-4 text-emerald-400" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">Coverage confirmed</p>
                  <p className="mt-1.5 text-sm text-white/90 leading-relaxed">
                    We're watching{" "}
                    <span className="font-medium text-emerald-300 break-all">{org.website}</span>
                    {" "}— every donation form we can find, plus up to 150 other pages for broken
                    links, SSL, uptime, and security issues.
                  </p>
                  <ul className="mt-3 grid sm:grid-cols-2 gap-y-1.5 gap-x-4 text-[12px] text-white/70">
                    <li className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      Donation link {org.donation_url ? <span className="text-white/90 font-medium">provided</span> : <span className="text-white/50">auto-detected from site</span>}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      Contact form {org.contact_form_url ? <span className="text-white/90 font-medium">provided</span> : <span className="text-white/50">auto-detected</span>}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      Social links: <span className="text-white/90 font-medium">{Object.values(org.social_links || {}).filter(Boolean).length}</span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      Extra links you added: <span className="text-white/90 font-medium">{(org.custom_links || []).filter((l) => (l.url || "").trim()).length}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {org && (
            <form onSubmit={saveOrg} className="grid sm:grid-cols-2 gap-4">
              {[
                ["organization_name", "Organization name", "Hope Foundation"],
                ["website", "Website URL", "https://…"],
                ["donation_url", "Donation page URL", "https://…/donate"],
                ["contact_form_url", "Contact form URL", "https://…/contact"],
              ].map(([k, label, ph]) => (
                <div key={k}>
                  <label className="text-xs font-medium text-charcoal-700">{label}</label>
                  <input value={org[k] || ""} onChange={setField(k)} placeholder={ph}
                    className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-black/10 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                    data-testid={`org-${k}`} />
                </div>
              ))}
              {[["facebook", "Facebook"], ["instagram", "Instagram"], ["linkedin", "LinkedIn"], ["x", "X / Twitter"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs font-medium text-charcoal-700">{label}</label>
                  <input value={org.social_links?.[k] || ""} onChange={setSocial(k)} placeholder="https://…"
                    className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-black/10 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                    data-testid={`social-${k}`} />
                </div>
              ))}
              <div className="sm:col-span-2 pt-2 border-t border-black/5" data-testid="custom-links-section">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-charcoal-800">More links to monitor</label>
                  <span className="text-[11px] font-mono text-charcoal-400">{(org.custom_links || []).length}/5</span>
                </div>
                <p className="text-xs text-charcoal-400 mb-3">Add anything else we should watch — event pages, volunteer forms, a store, a blog. Each one is checked on every scan.</p>
                <div className="space-y-2.5">
                  {(org.custom_links || []).map((link, i) => (
                    <div key={i} className="flex flex-col sm:flex-row gap-2.5" data-testid="custom-link-row">
                      <input value={link.label || ""} onChange={setCustomLink(i, "label")} placeholder="Label (e.g. Volunteer form)"
                        className="sm:w-52 h-11 px-3.5 rounded-xl border border-black/10 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                        data-testid={`custom-link-label-${i}`} />
                      <input value={link.url || ""} onChange={setCustomLink(i, "url")} placeholder="https://…"
                        className="flex-1 h-11 px-3.5 rounded-xl border border-black/10 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                        data-testid={`custom-link-url-${i}`} />
                      <button type="button" onClick={() => removeCustomLink(i)}
                        className="h-11 w-11 shrink-0 grid place-items-center rounded-xl border border-black/10 text-charcoal-300 hover:text-red-500 hover:border-red-200 transition-colors"
                        data-testid={`remove-custom-link-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {(org.custom_links || []).length < 5 && (
                  <button type="button" onClick={addCustomLink}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm px-3.5 py-2 rounded-lg border border-dashed border-black/15 text-charcoal-600 hover:border-brand hover:text-brand transition-colors"
                    data-testid="add-custom-link-btn">
                    <Plus className="w-4 h-4" /> Add more links
                  </button>
                )}
              </div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={savingOrg}
                  className="h-11 px-6 rounded-xl bg-brand text-white text-sm font-medium hover:bg-brand-hover transition-colors disabled:opacity-60"
                  data-testid="save-org-btn">
                  {savingOrg ? "Saving…" : "Save profile"}
                </button>
              </div>
            </form>
          )}
        </section>

        {/* team members (Complete plan) */}
        <section className="mt-6 rounded-2xl bg-white border border-black/5 shadow-soft p-6" data-testid="team-card">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-brand" />
            <h2 className="text-lg font-medium text-charcoal-900">Team members</h2>
            {!team.is_complete && (
              <span className="ml-2 text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-charcoal-100 text-charcoal-500">Protection plan</span>
            )}
          </div>
          <p className="text-sm text-charcoal-500 mb-5">People who receive the monthly health report by email.</p>

          {team.is_complete ? (
            <>
              <form onSubmit={addTeam} className="flex flex-col sm:flex-row gap-2.5 mb-4 max-w-xl">
                <input type="email" value={newMember} onChange={(e) => setNewMember(e.target.value)}
                  placeholder="teammate@nonprofit.org"
                  className="flex-1 h-11 px-3.5 rounded-xl border border-black/10 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                  data-testid="team-email" />
                <button type="submit" className="h-11 px-5 rounded-xl bg-charcoal-900 text-white text-sm font-medium hover:bg-charcoal-800 transition-colors inline-flex items-center justify-center gap-1.5" data-testid="add-team-btn">
                  <Plus className="w-4 h-4" /> Invite
                </button>
              </form>
              <div className="space-y-2 max-w-xl">
                {(team.members?.length ? team.members : team.recipients.map((e) => ({ email: e }))).length === 0 && <p className="text-sm text-charcoal-400">No team members yet.</p>}
                {(team.members?.length ? team.members : team.recipients.map((e) => ({ email: e }))).map((m) => (
                  <div key={m.email} className="flex flex-wrap items-center justify-between gap-2 py-2.5 px-3.5 rounded-lg bg-charcoal-50/70 border border-black/5" data-testid="team-item">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Mail className="w-4 h-4 text-charcoal-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-charcoal-800 truncate">{m.email}</p>
                        {(m.invited_at || m.invite_email_failed) && (
                          <p className="text-[11px] font-mono text-charcoal-400 mt-0.5">
                            {m.invite_email_failed
                              ? <span className="text-amber-600">Invite delivery failed</span>
                              : `Invited ${new Date(m.invited_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => resendInvite(m.email)}
                        disabled={resendingInvite === m.email}
                        className="text-[11px] font-medium text-brand hover:underline disabled:opacity-60"
                        data-testid="resend-invite-btn"
                      >
                        {resendingInvite === m.email ? "Sending…" : "Resend invite"}
                      </button>
                      <button onClick={() => removeTeam(m.email)} className="text-charcoal-300 hover:text-red-500 transition-colors" data-testid="remove-team-btn">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-charcoal-50/70 border border-dashed border-black/10 px-5 py-4">
              <p className="text-sm text-charcoal-500">Upgrade to Protection to add multiple team members who receive reports.</p>
              <button onClick={() => openUpgradeModal("team")} className="text-sm font-medium text-brand hover:underline inline-flex items-center gap-1" data-testid="team-upgrade">
                Upgrade <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </section>

        {/* verification badge */}
        {active && verification?.token && (
          <section className="mt-6 rounded-2xl bg-white border border-black/5 shadow-soft p-6" data-testid="verification-card">
            <div className="flex items-center gap-2 mb-1">
              <Award className="w-4 h-4 text-brand" />
              <h2 className="text-lg font-medium text-charcoal-900">Verification badge</h2>
            </div>
            <p className="text-sm text-charcoal-500 mb-5">Show donors your systems are monitored. Add this badge to your website — it links to your public verification page.</p>
            <div className="grid md:grid-cols-2 gap-5 items-start">
              <div className="flex justify-center rounded-xl bg-charcoal-50/70 border border-black/5 p-6">
                <a href={`/verify/${verification.token}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-3 rounded-xl bg-white border border-black/10 shadow-soft px-4 py-3 hover:shadow-premium transition-shadow" data-testid="badge-preview">
                  <div className="w-9 h-9 rounded-lg bg-charcoal-900 grid place-items-center">
                    <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-[11px] font-mono uppercase tracking-wider text-charcoal-400 leading-none">Mission Watch Verified</p>
                    <p className="text-sm font-semibold text-charcoal-900 leading-tight mt-1">Score {verification.score ?? "—"}</p>
                  </div>
                </a>
              </div>
              <div>
                <label className="text-xs font-medium text-charcoal-700">Copy this snippet into your website</label>
                <textarea readOnly rows={4} data-testid="badge-embed-code"
                  value={`<a href="${window.location.origin}/verify/${verification.token}" target="_blank" style="display:inline-flex;align-items:center;gap:10px;font-family:sans-serif;text-decoration:none;border:1px solid #e5e5e5;border-radius:12px;padding:10px 14px;background:#fff;">\n  <span style="display:inline-block;width:34px;height:34px;border-radius:8px;background:#09090b;color:#34d399;text-align:center;line-height:34px;">&#10003;</span>\n  <span style="text-align:left;"><span style="display:block;font-size:10px;letter-spacing:1px;text-transform:uppercase;color:#a1a1aa;">Mission Watch Verified</span><span style="display:block;font-size:14px;font-weight:600;color:#09090b;">Score ${verification.score ?? "—"}</span></span>\n</a>`}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-black/10 text-xs font-mono text-charcoal-600 outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition" />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`<a href="${window.location.origin}/verify/${verification.token}" target="_blank" style="display:inline-flex;align-items:center;gap:10px;font-family:sans-serif;text-decoration:none;border:1px solid #e5e5e5;border-radius:12px;padding:10px 14px;background:#fff;"><span style="display:inline-block;width:34px;height:34px;border-radius:8px;background:#09090b;color:#34d399;text-align:center;line-height:34px;">&#10003;</span><span style="text-align:left;"><span style="display:block;font-size:10px;letter-spacing:1px;text-transform:uppercase;color:#a1a1aa;">Mission Watch Verified</span><span style="display:block;font-size:14px;font-weight:600;color:#09090b;">Score ${verification.score ?? "—"}</span></span></a>`);
                    toast.success("Badge code copied to clipboard");
                  }}
                  className="mt-2.5 inline-flex items-center gap-1.5 text-sm px-3.5 py-2 rounded-lg bg-charcoal-900 text-white hover:bg-charcoal-800 transition-colors"
                  data-testid="copy-badge-btn">
                  <Copy className="w-3.5 h-3.5" /> Copy badge code
                </button>
                <a href={`/verify/${verification.token}`} target="_blank" rel="noreferrer"
                  className="ml-2 inline-flex items-center gap-1 text-sm text-brand hover:underline" data-testid="view-verification-link">
                  View verification <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </section>
        )}


        {/* Account details — signup date, plan, renewal date, last sign-in */}
        <AccountDetails user={user} subscription={subscription} />

        {/* Notification preferences — client-side alert opt-outs */}
        <NotificationPreferences />

        {/* Monitoring exclusions — per-org URL patterns to ignore in scoring */}
        <MonitoringExclusions />

        {/* Support footer — always available. mailto opens the user's mail
            client pre-addressed to admin@missionwatch.app. */}
        <div className="mt-8 pt-6 border-t border-black/5 flex flex-wrap items-center justify-between gap-3 text-xs text-charcoal-400 font-mono" data-testid="dashboard-support-footer">
          <p>Need help? A real person answers every message.</p>
          <a
            href="mailto:admin@missionwatch.app?subject=Mission%20Watch%20Support"
            className="inline-flex items-center gap-1.5 text-brand hover:underline"
            data-testid="dashboard-contact-link"
          >
            <LifeBuoy className="w-3.5 h-3.5" /> Contact support
          </a>
        </div>

        {!active && (
          <div className="mt-6 text-center">
            <button onClick={() => navigate("/get-started")} className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline" data-testid="footer-upgrade">
              Start protecting my organization <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
      <UpgradeNudge
        open={upgradeNudge}
        onClose={() => setUpgradeNudge(false)}
        reason={upgradeNudgeReason}
        onUpgradeComplete={onUpgradeComplete}
      />
      <UpgradeSuccess open={upgradeSuccess} onClose={() => setUpgradeSuccess(false)} />
      <ChangePasswordModal open={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </div>
  );
}
